/* eslint-disable react-hooks/exhaustive-deps */
import { Spin, Typography } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useState, useEffect } from "react";

import SalesAnalysisGraphs from "./SalesAnalysisGraphs";
import SalesFilters from "./SalesFilters";
import SalesStatistics from "./SaleStatistics";
import SalesTransactionsTable from "./SalesTransactionsTable";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";

const { Title } = Typography;
const PRIME_COURSE_NAMES = ["Prime Foundation", "Prime Pro"];

const SalesAnalytics = ({ ROOT_URL }) => {
  const [startDate, setStartDate] = useState(dayjs("2025-04-19").startOf("day").toDate());
  const [endDate, setEndDate] = useState(dayjs().endOf("day").toDate());
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedCoupons, setSelectedCoupons] = useState([]);
  const [selectedAnalyticsTypes, setSelectedAnalyticsTypes] = useState([]);
  const [showRefunded, setShowRefunded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [availableAnalyticsTypes, setAvailableAnalyticsTypes] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [stats, setStats] = useState({
    totalAmount: 0,
    bundledCount: 0,
    individualCount: 0,
    discountedCount: 0,
    totalCount: 0,
    rawCount: 0,
    dailyAverageAmount: 0,
    dailyAverageCount: 0
  });
  const [dailyPaymentAmountGraphPoints, setDailyPaymentAmountGraphPoints] = useState([]);
  const [dailyPaymentCountGraphPoints, setDailyPaymentCountGraphPoints] = useState([]);
  const [hourlyPaymentCountGraphPoints, setHourlyPaymentCountGraphPoints] = useState([]);
  const [weekdayPaymentCountGraphPoints, setWeekdayPaymentCountGraphPoints] = useState([]);

  const handleDownloadCSV = async () => {
    try {
      const filter = {
        startDate: dayjs(startDate).startOf("day").format(),
        endDate: dayjs(endDate).endOf("day").format(),
        courses: selectedCourses,
        coupons: selectedCoupons,
        selectedAnalyticsTypes,
        showRefunded
      };

      const URL = `${ROOT_URL}/admin/payments/sales/export`;
      const response = await axios.post(URL, filter, { responseType: "blob" });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      const filename = `sales_data_${dayjs(startDate).format("YYYY-MM-DD")}_to_${dayjs(
        endDate
      ).format("YYYY-MM-DD")}.csv`;

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccessNotification("CSV downloaded successfully");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      showErrorNotification("Failed to download CSV. Please try again");
    }
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleDateChange = (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setCurrentPage(1);
  };

  const handleCoursesChange = (courses) => {
    setSelectedCourses(courses);
    setCurrentPage(1);
  };

  const handleCouponsChange = (coupons) => {
    setSelectedCoupons(coupons);
    setCurrentPage(1);
  };

  const handleAnalyticsTypeChange = (analyticsTypes) => {
    setSelectedAnalyticsTypes(analyticsTypes);
    setCurrentPage(1);
  };

  const handleShowRefundedChange = (value) => {
    setShowRefunded(value);
    setCurrentPage(1);
  };

  const loadSalesAnalytics = async () => {
    try {
      setIsLoading(true);
      const filter = {
        startDate: dayjs(startDate).startOf("day").format(),
        endDate: dayjs(endDate).endOf("day").format(),
        courses: selectedCourses,
        coupons: selectedCoupons,
        selectedAnalyticsTypes: selectedAnalyticsTypes,
        showRefunded: showRefunded,
        page: currentPage,
        pageSize: pageSize
      };

      const URL = `${ROOT_URL}/admin/payments/sales`;
      const { data } = await axios.post(URL, filter);
      setAvailableCourses(
        data.filters.courses.filter(
          ({ name }) => name.includes("Self-Paced") || PRIME_COURSE_NAMES.includes(name)
        )
      );
      setAvailableCoupons(data.filters.coupons);
      setAvailableAnalyticsTypes(data.filters.analyticsTypes);
      setPayments(data.payments);
      setPagination(data.pagination);
      setStats(data.stats);
      setDailyPaymentAmountGraphPoints(data.dailyPaymentAmountGraphPoints);
      setDailyPaymentCountGraphPoints(data.dailyPaymentCountGraphPoints);
      setHourlyPaymentCountGraphPoints(data.hourlyPaymentCountGraphPoints);
      setWeekdayPaymentCountGraphPoints(data.weekdayPaymentCountGraphPoints);

      showSuccessNotification("Sales analytics loaded successfully.");
    } catch (error) {
      console.error("Error loading sales data:", error);
      showErrorNotification("Failed to load sales analytics. Please try again");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSalesAnalytics();
  }, [
    startDate,
    endDate,
    selectedCourses,
    selectedAnalyticsTypes,
    selectedCoupons,
    showRefunded,
    currentPage,
    pageSize,
    ROOT_URL
  ]);

  return (
    <>
      <div style={{ padding: clamp(12, 20) }}>
        <Title level={2}>Sales Analytics</Title>

        <SalesFilters
          startDate={startDate}
          endDate={endDate}
          availableCourses={availableCourses}
          availableCoupons={availableCoupons}
          availableAnalyticsTypes={availableAnalyticsTypes}
          onDateChange={handleDateChange}
          onCoursesChange={handleCoursesChange}
          onCouponsChange={handleCouponsChange}
          onAnalyticsTypeChange={handleAnalyticsTypeChange}
          showRefunded={showRefunded}
          onShowRefundedChange={handleShowRefundedChange}
        />

        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              width: "100%"
            }}
          >
            <Spin size="large" tip="Loading Payment Analytics..." />
          </div>
        ) : (
          <>
            <SalesStatistics stats={stats} />
            <SalesAnalysisGraphs
              dailyPaymentAmountGraphPoints={dailyPaymentAmountGraphPoints}
              dailyPaymentCountGraphPoints={dailyPaymentCountGraphPoints}
              hourlyPaymentCountGraphPoints={hourlyPaymentCountGraphPoints}
              weekdayPaymentCountGraphPoints={weekdayPaymentCountGraphPoints}
            />
            <SalesTransactionsTable
              payments={payments}
              pagination={pagination}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onDownloadCSV={handleDownloadCSV}
              selectedAnalyticsTypes={selectedAnalyticsTypes}
            />
          </>
        )}
      </div>
    </>
  );
};

export default SalesAnalytics;
